import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { useApolloClient, useMutation } from '@apollo/client/react';
import log from 'loglevel';
import { useNavigate } from 'react-router-dom';

import { purgeApolloPersistedCache } from '../apollo/cachePersistence';
import { GET_MY_TENANTS, SELECT_TENANT_MUTATION } from '../graphql/tenant.operations';
import { useAuth } from '../hooks/useAuth';
import { setSentryTenant } from '../sentry';
import { Tenant, TenantContextType, UserTenant } from '../types/tenant.types';
import type { UntypedData } from '../apollo/untypedData';

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// Key for storing the last logged-in user ID
const LAST_USER_ID_KEY = 'lastLoggedInUserId';

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<UserTenant[]>([]);
  const tenantsLoadedOnce = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [isRestoringContext, setIsRestoringContext] = useState(false);

  // Track if we've already restored context to prevent infinite loops
  // Use sessionStorage instead of ref to persist across component remounts
  const RESTORE_FLAG_KEY = 'tenant-context-restored';

  // Track if we've already handled user change detection for this session
  const userChangeHandled = useRef(false);

  const client = useApolloClient();
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();

  const [selectTenantMutation] = useMutation<UntypedData>(SELECT_TENANT_MUTATION);

  // Restore tenant context with retry logic
  const restoreTenantContext = useCallback(
    async (tenantId: string, retryCount = 0): Promise<boolean> => {
      if (retryCount >= 3) {
        log.error('Max retries reached for tenant context restoration');
        return false;
      }

      try {
        setIsRestoringContext(true);
        log.info(`Attempting to restore tenant context (attempt ${retryCount + 1}):`, tenantId);

        await selectTenantMutation({
          variables: { input: { tenantId } },
        });

        log.info('Tenant context restored successfully');
        return true;
      } catch (err) {
        log.warn(`Failed to restore tenant context (attempt ${retryCount + 1}):`, err);

        // Retry with exponential backoff
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        await new Promise((resolve) => setTimeout(resolve, delay));

        return restoreTenantContext(tenantId, retryCount + 1);
      } finally {
        setIsRestoringContext(false);
      }
    },
    [selectTenantMutation],
  );

  // Handle logout with cleanup
  const handleLogout = useCallback(async () => {
    // Clear ALL auth state to prevent stuck sessions and tenant data leakage
    log.info('🔐 Logging out - clearing all tenant and auth state');

    // Clear all storage to ensure clean state for next login
    localStorage.clear();
    sessionStorage.clear();

    // Reset all React state
    setSelectedTenant(null);
    setAvailableTenants([]);

    // Reset user change tracking for next session
    userChangeHandled.current = false;

    // Fully tear down cached tenant data — both the in-memory Apollo store and
    // the persisted IndexedDB cache — BEFORE redirecting, so nothing survives
    // into the next session (the redirect can otherwise cancel pending deletes).
    try {
      await client.clearStore();
    } catch (err) {
      log.error('Failed to clear Apollo cache on logout:', err);
    }
    await purgeApolloPersistedCache();

    logout({ returnTo: window.location.origin });
  }, [logout, client]);

  // Load tenants on mount
  const loadTenants = useCallback(async (): Promise<UserTenant[]> => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return [];
    }

    try {
      setError(null);

      const { data } = await client.query<UntypedData>({
        query: GET_MY_TENANTS,
        fetchPolicy: 'network-only', // Always fetch fresh data
      });

      const tenants = data.myTenants || [];
      setAvailableTenants(tenants);
      tenantsLoadedOnce.current = true;

      // Check for previously selected tenant with fallback to sessionStorage
      let storedTenantId =
        localStorage.getItem('selectedTenantId') || sessionStorage.getItem('selectedTenantId');

      // Safety: If stored value is not a valid ObjectId string, try to extract the id
      if (storedTenantId) {
        try {
          // If it's a JSON object (old format), extract the id
          if (storedTenantId.startsWith('{') || storedTenantId.includes('ObjectId')) {
            log.warn(
              'Detected malformed stored tenant ID, attempting to fix:',
              storedTenantId.substring(0, 50),
            );

            // Try to extract ObjectId from malformed string like "new ObjectId('...')"
            const objectIdMatch = storedTenantId.match(/ObjectId\(['"]([a-f0-9]{24})['"]\)/i);
            if (objectIdMatch) {
              storedTenantId = objectIdMatch[1];
              log.info('Extracted ObjectId:', storedTenantId);
            } else {
              // Try JSON parsing
              const parsed = JSON.parse(storedTenantId);
              // Handle various ID formats (id, _id, or ObjectId)
              if (parsed.id) {
                storedTenantId = typeof parsed.id === 'string' ? parsed.id : String(parsed.id);
              } else if (parsed._id) {
                storedTenantId = typeof parsed._id === 'string' ? parsed._id : String(parsed._id);
              } else {
                storedTenantId = null;
              }
            }

            if (storedTenantId && storedTenantId.length === 24) {
              // Update storage with correct format
              localStorage.setItem('selectedTenantId', storedTenantId);
              sessionStorage.setItem('selectedTenantId', storedTenantId);
              log.info('✅ Migrated stored tenant ID to correct format:', storedTenantId);
            } else {
              log.error('Failed to extract valid ObjectId, clearing storage');
              localStorage.removeItem('selectedTenantId');
              sessionStorage.removeItem('selectedTenantId');
              storedTenantId = null;
            }
          }
        } catch (e) {
          log.warn('Failed to parse stored tenant ID, clearing:', e);
          localStorage.removeItem('selectedTenantId');
          sessionStorage.removeItem('selectedTenantId');
          storedTenantId = null;
        }
      }

      if (storedTenantId) {
        const userTenant = tenants.find((ut: UserTenant) => ut.tenantId === storedTenantId);
        if (userTenant) {
          setSelectedTenant(userTenant.tenant);

          // Ensure server-side context is set with retry logic
          restoreTenantContext(storedTenantId).catch((err) => {
            log.error('Failed to restore tenant context on server:', err);
            // Don't clear the tenant immediately - let the error handler deal with it
          });
        } else {
          // Stored tenant no longer accessible, clear it from both storages
          localStorage.removeItem('selectedTenantId');
          sessionStorage.removeItem('selectedTenantId');
          setSelectedTenant(null);
        }
      } else if (tenants.length === 1) {
        // Auto-select if only one tenant available
        const singleTenant = tenants[0];
        localStorage.setItem('selectedTenantId', singleTenant.tenantId);
        sessionStorage.setItem('selectedTenantId', singleTenant.tenantId);
        setSelectedTenant(singleTenant.tenant);

        // Set the server-side context with retry logic
        restoreTenantContext(singleTenant.tenantId).catch((err) => {
          log.error('Failed to set tenant context on server:', err);
        });
      }

      return tenants;
    } catch (err) {
      log.error('Failed to load tenants:', err);
      setError(err as Error);

      // If we can't load tenants, user might have lost access - log them out
      const message = err instanceof Error ? err.message : String(err);
      if (message.includes('Unauthorized') || message.includes('Forbidden')) {
        handleLogout();
      }

      throw err;
    }
  }, [client, isAuthenticated, handleLogout, restoreTenantContext]);

  // Select tenant and navigate
  const selectTenant = useCallback(
    async (tenantId: string) => {
      try {
        setError(null);

        // Call backend mutation
        await selectTenantMutation({
          variables: { input: { tenantId } },
        });

        // Store in both localStorage and sessionStorage for redundancy
        localStorage.setItem('selectedTenantId', tenantId);
        sessionStorage.setItem('selectedTenantId', tenantId);

        // Update local state
        const userTenant = availableTenants.find((ut) => ut.tenantId === tenantId);
        if (userTenant) {
          setSelectedTenant(userTenant.tenant);
        }

        // Navigate to dashboard
        navigate('/dashboard');
      } catch (err) {
        log.error('Failed to select tenant:', err);
        setError(err as Error);

        // If tenant selection fails due to access issues, log out
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes('access') || message.includes('Forbidden')) {
          handleLogout();
        }

        throw err;
      }
    },
    [availableTenants, selectTenantMutation, navigate, handleLogout],
  );

  // Switch tenant and reload page (clear all cache)
  const switchTenant = useCallback(
    async (tenantId: string) => {
      try {
        setError(null);

        await selectTenantMutation({
          variables: { input: { tenantId } },
        });

        localStorage.setItem('selectedTenantId', tenantId);
        sessionStorage.setItem('selectedTenantId', tenantId);

        // Clear Apollo cache but preserve tenant data
        await client.clearStore();

        // Drop the persisted cache too — it is scoped to the previous tenant.
        // The new tenant re-fetches and re-persists fresh on the next load.
        await purgeApolloPersistedCache();

        // Restore tenant data after cache clear
        const userTenant = availableTenants.find((ut) => ut.tenantId === tenantId);
        if (userTenant) {
          setSelectedTenant(userTenant.tenant);
        }

        window.location.href = '/dashboard';
      } catch (err) {
        log.error('Failed to switch tenant:', err);
        setError(err as Error);

        // If tenant switch fails due to access issues, log out
        const message = err instanceof Error ? err.message : String(err);
        if (message.includes('access') || message.includes('Forbidden')) {
          handleLogout();
        }

        throw err;
      }
    },
    [selectTenantMutation, client, handleLogout, availableTenants],
  );

  // Detect user change and clear stale state
  // This is critical for multi-tenant security - when a different user logs in,
  // we must clear all cached tenant data to prevent data leakage between users
  useEffect(() => {
    if (!isAuthenticated || !user?.sub || userChangeHandled.current) {
      return;
    }

    // Guard synchronously so the effect can't re-enter while the async cleanup
    // below is in flight.
    userChangeHandled.current = true;

    const currentUserId = user.sub;
    const lastUserId = localStorage.getItem(LAST_USER_ID_KEY);

    // Check if a different user is logging in
    if (lastUserId && lastUserId !== currentUserId) {
      log.warn(
        `🔐 User change detected! Previous: ${lastUserId}, Current: ${currentUserId}`,
      );
      log.warn('🔐 Clearing cached tenant selection to prevent data leakage');

      // Clear tenant-related storage (but don't clear availableTenants — loadTenants will refresh)
      localStorage.removeItem('selectedTenantId');
      sessionStorage.removeItem('selectedTenantId');
      sessionStorage.removeItem(RESTORE_FLAG_KEY);
      setSelectedTenant(null);

      // Fully clear the previous user's cached data — both the in-memory Apollo
      // store and the persisted IndexedDB cache — and only record the new
      // identity once that completes, so the new session can't observe or
      // re-persist the previous user's data.
      void (async () => {
        try {
          await client.clearStore();
        } catch (err) {
          log.error('Failed to clear Apollo cache on user change:', err);
        }
        await purgeApolloPersistedCache();
        localStorage.setItem(LAST_USER_ID_KEY, currentUserId);
      })();
      return;
    }

    // Same user (or first login this browser) — record for future comparison.
    localStorage.setItem(LAST_USER_ID_KEY, currentUserId);
  }, [isAuthenticated, user?.sub, client]);

  // Load tenants when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      if (!tenantsLoadedOnce.current) {
        setIsLoading(true);
      }
      loadTenants()
        .catch(log.error)
        .finally(() => {
          setIsLoading(false);
        });
    } else if (tenantsLoadedOnce.current) {
      // Only clear state after we've loaded at least once (user logged out)
      setIsLoading(false);
      setSelectedTenant(null);
      setAvailableTenants([]);
      tenantsLoadedOnce.current = false;
    }
  }, [isAuthenticated, loadTenants]);

  // Keep Sentry's tenantId tag on the *active* tenant. This lives here rather
  // than in LocalAuthProvider because `selectTenant` switches tenant without a
  // token refresh, so the JWT claim goes stale — and because LocalAuthProvider
  // sits above this provider and cannot read tenant context.
  useEffect(() => {
    setSentryTenant(selectedTenant?.id ?? null);
  }, [selectedTenant?.id]);

  const value: TenantContextType = {
    selectedTenant,
    availableTenants,
    selectTenant,
    switchTenant,
    loadTenants,
    restoreTenantContext,
    isLoading,
    isRestoringContext,
    error,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};
